/**
 * Hex color helpers. These live here rather than in `scenes/engine/utils/utils`
 * (which re-exports them for back-compat) so tier-1 primitives in
 * `src/components` can use them without pulling `fabric` into their bundle.
 */

/**
 * `#RGB` / `#RRGGBB` (with or without the hash) -> channel values. Returns
 * `null` for anything else rather than a `NaN` triple.
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove the hash at the start if it's there
  const cleanHex = String(hex ?? '')
    .trim()
    .replace(/^#/, '')
  const expanded =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map(channel => channel + channel)
          .join('')
      : // `#RRGGBBAA` project colors: drop the alpha, keep the channels.
        cleanHex.slice(0, 6)
  if (!/^[0-9a-f]{6}$/i.test(expanded)) {
    return null
  }
  // Parse the r, g, b values
  const bigint = parseInt(expanded, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return { r, g, b }
}

/**
 * `#RRGGBB` -> `rgba(r, g, b, alpha)`. Anything that is not a 6-digit hex
 * (`'transparent'`, an existing `rgba()`, a gradient) is returned unchanged
 * instead of producing `rgba(NaN, NaN, NaN, …)`.
 */
export const hexToRgba = (hex: string, alpha = 1): string => {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) {
    return hex
  }
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const hexToHexa = (hex: string, alpha = 1): string => {
  // Remove # if present
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex

  // Convert alpha from decimal (0-1) to hex (00-FF)
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0')

  // Return hex value with alpha component
  return `#${cleanHex}${alphaHex}`
}

/**
 * Perceived brightness test. Unparseable input reports "not bright" rather than
 * throwing — callers use this to pick a contrasting text color while rendering
 * project data, where a bad color must not break the render.
 */
export function isColorBright(hex: string): boolean {
  const rgb = hexToRgb(hex)
  if (!rgb) {
    return false
  }
  // Calculate brightness (standard formula)
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
  // Return true if the color is bright, false if dark
  return brightness > 128
}
